/**
 * The motocross bike: its model, its physics, its suspension, and the rider.
 *
 * **The model** is a 450-class motocrosser at real scale — 1.51 m between
 * the axles, a 21-inch front wheel and an 18-inch rear, a seat at 0.96 m —
 * built the way the car is: flat-shaded facets and crisp, saturated parts.
 * Its colours are the player's own: plastics in the shirt's orange, seat and
 * frame in the trousers' navy, number plates in the shoes' white. The detail
 * is where a bike is read from: knobbly tyres, laced spokes, wavy brake
 * discs, upside-down forks with gold inner tubes, a coil-over shock, the
 * chain, the header curling round to a high silencer.
 *
 * **The suspension** is long-travel and visible: 300 mm at each end. The
 * forks slide, the swingarm swings and the shock's spring squeezes, from a
 * sprung chassis over two wheels that follow the ground under them. Braking
 * dives the forks, power squats the rear, and a landing drives both into
 * their stops and bounces back out.
 *
 * **The physics** is a bike's, not a car's: it steers by leaning. The bars
 * choose a turn, the bike leans to hold it — as far as the tyres' grip
 * allows — and the turn follows the lean. At a crawl the bars steer
 * directly. Off the road the knobblies still bite, just less.
 *
 * **The rider** sits in the corners and stands over rough ground and in the
 * air, puts the inside foot out in a tight turn and a foot down when
 * stopped; the hands stay on the grips and the feet on the pegs, solved by
 * the crowd's inverse kinematics (`CharacterCrowd.placeRider`).
 */

import {
  BoxGeometry,
  CircleGeometry,
  CatmullRomCurve3,
  Color,
  CylinderGeometry,
  Euler,
  ExtrudeGeometry,
  Group,
  IcosahedronGeometry,
  InstancedMesh,
  LatheGeometry,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Path,
  Quaternion,
  Shape,
  SpotLight,
  TubeGeometry,
  Vector2,
  Vector3,
} from 'three';
import type { BufferGeometry, Material } from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { RiderPose } from './character/crowd.js';
import type { Surface } from './Vehicle.js';

// --- geometry -------------------------------------------------------------------
// The bike's frame: x right, y up, z back (forward is -Z), origin on the
// ground midway between the axles, everything at static sag.

/** Steering axis tilt from vertical: 27°. */
const RAKE = 0.471;
/** The steering head, where the axis meets the frame. */
const HEAD = new Vector3(0, 1.05, -0.42);
/** Head to front axle along the fork, at sag. */
const FORK_AT_SAG = 0.774;
/** Swingarm pivot, and the rear axle from it at sag. */
const PIVOT = new Vector3(0, 0.53, 0.12);
const ARM = new Vector3(0, -0.185, 0.62);
const ARM_LENGTH = Math.hypot(ARM.y, ARM.z);
/** The shock: its top on the frame, its bottom on the swingarm (from the pivot). */
const SHOCK_TOP = new Vector3(0, 0.84, 0.17);
const SHOCK_FOOT = new Vector3(0, -0.08, 0.2);
/** A grip in the steering's own (tilted) frame; the right one, the left mirrored. */
const GRIP = new Vector3(0.37, 0.152, 0.012);
/** A footpeg, right side. */
const PEG = new Vector3(0.2, 0.4, 0.16);

export const MOTO = {
  kind: 'moto',
  wheelbase: 1.51,
  /** Steering axis tilt from vertical, radians. */
  rake: RAKE,
  /** Axle positions along the bike, metres, forward negative. */
  frontAxleZ: HEAD.z - Math.sin(RAKE) * FORK_AT_SAG,
  rearAxleZ: PIVOT.z + ARM.z,
  frontRadius: 0.36,
  rearRadius: 0.345,
  frontTyre: 0.09,
  rearTyre: 0.125,
  /** Suspension travel and static sag, metres: along the fork, and at the rear axle. */
  forkTravel: 0.3,
  rearTravel: 0.31,
  frontSag: 0.07,
  rearSag: 0.1,
  /** The rider's eyes above the ground, seated, for the first-person view. */
  eyeHeight: 1.56,
} as const satisfies TwoWheelerGeometry;

/** A two-wheeler's frame: where its wheels are and how far its springs go. */
export interface TwoWheelerGeometry {
  kind: 'moto' | 'scooter';
  wheelbase: number;
  rake: number;
  frontAxleZ: number;
  rearAxleZ: number;
  frontRadius: number;
  rearRadius: number;
  frontTyre: number;
  rearTyre: number;
  forkTravel: number;
  rearTravel: number;
  frontSag: number;
  rearSag: number;
  eyeHeight: number;
}

/** How a two-wheeler pulls, stops, grips and leans: see `RIDE` for what each is. */
export type RideSpec = { -readonly [K in keyof typeof RIDE]: number };
export type WheelieSpec = { -readonly [K in keyof typeof WHEELIE]: number };

/**
 * Everything the riding physics, the suspension and the controller need to
 * know about a two-wheeler: the motocrosser's (`MOTO_SPEC`), or the
 * e-scooter's (`three/Scooter.ts`). The same `stepMotorbike` rides both.
 */
export interface TwoWheelerSpec {
  geometry: TwoWheelerGeometry;
  ride: RideSpec;
  /** How it lifts its front wheel; none, and it never does. */
  wheelie?: WheelieSpec;
  /** Tarmac, and off the road. */
  road: Surface;
  dirt: Surface;
  /** Share of g it falls at in the air: the bike floats a touch, for its jumps. */
  airGravity: number;
  /** Its collision: two circles of `radius`, `circleOffset` ahead of and behind its middle, striking at `strikeHeight`. */
  radius: number;
  circleOffset: number;
  strikeHeight: number;
  /** The sprung chassis: how fast it bobs (rad/s) in heave and in pitch. */
  heave: number;
  pitchRate: number;
}

export const RIDE = {
  /** Pull from rest, m/s²: 0–100 km/h in about four seconds, 150 km/h flat out. */
  power: 8.5,
  brake: 10.5,
  /** Paddling it backwards, m/s. */
  reverseMax: 1.6,
  /** The engine pulls toward this, m/s; drag holds it to about 150 km/h. */
  maxSpeed: 44,
  drag: 0.00036,
  rolling: 0.25,
  /** A big single engine-brakes hard off the throttle. */
  engineBrake: 1.6,
  /** Handlebar lock, radians, and the turn it gives at a crawl. */
  steerLock: 0.62,
  /** Most sideways acceleration the tyres hold, m/s², on tarmac. */
  cornerGrip: 11,
  maxLean: 0.9,
  /** How briskly the bike lays into a lean and comes back up, rad/s. */
  leanRate: 9,
  lateralGrip: 11,
  /** ... with the rear brake stamped on (Space): the back steps out. */
  slideGrip: 1.6,
  slideBrake: 3.5,
  /** Steepest slope the brakes hold it on, as an acceleration, m/s². */
  parkingHold: 3,
  gravity: 9.81,
} as const;

/**
 * The wheelie: the front lifts when the rear wheel pushes harder than the
 * bike's weight holds the nose down, pivoting on the rear axle. Angles are
 * radians above the ground's pitch, accelerations rad/s².
 *
 * Flat out from a standstill the front comes up by itself. Held flat out,
 * the bike shifts up after a moment and the front comes back down, so a
 * launch wheelie is short. A wheelie that lasts is ridden on the throttle:
 * snapping it open lifts the front (a clutch-up), and the weight pulling the
 * nose down weakens toward the balance point, so blips keep it up without a
 * gear change. Letting go brings it down; either brake brings it down at
 * once. It never loops: the tail's limit stops it.
 */
export const WHEELIE = {
  /** Lift per m/s² of drive, at a standstill. */
  lift: 1.5,
  /** The drive's lift fades with speed, to nothing here, m/s. */
  fadeSpeed: 28,
  /** Held open this long the bike shifts up and the lift goes, s. */
  shiftAfter: 0.6,
  shiftTime: 0.35,
  /** Weight pulling the nose down, level, and the angle where it would vanish. */
  gravity: 9.5,
  balance: 1.6,
  /** Extra pull down with the throttle shut, and with a brake on. */
  release: 3,
  brake: 16,
  damping: 3.2,
  /** Snapping the throttle open: a clutch-up, rad/s. */
  kick: 4,
  /** ... below this speed, m/s. */
  kickSpeed: 22,
  max: 0.72,
} as const;

/** Tarmac, and dirt: knobblies bite in the dirt, just less, and the throttle digs in. */
export const MOTO_ROAD: Surface = { grip: 1, rolling: RIDE.rolling, topSpeed: 1 };
export const MOTO_DIRT: Surface = { grip: 0.82, rolling: 0.7, topSpeed: 0.85 };

/** The motocrosser, as the physics and the controller take it. */
export const MOTO_SPEC: TwoWheelerSpec = {
  geometry: MOTO, ride: RIDE, wheelie: WHEELIE, road: MOTO_ROAD, dirt: MOTO_DIRT,
  airGravity: 0.88, radius: 0.4, circleOffset: 0.5, strikeHeight: 0.75, heave: 10, pitchRate: 11,
};

export interface RideInput {
  /** -1 (brake, then paddle back) to 1 (throttle). */
  throttle: number;
  /** -1 (left) to 1 (right). */
  steer: number;
  /** Rear brake locked: the back comes round. */
  slide: boolean;
}

export interface MotoState {
  /** Signed speed along the heading, m/s. */
  speed: number;
  /** Lean, radians, positive to the right. */
  lean: number;
  leanRate: number;
  /** Handlebar angle, radians, positive right: what the bars show. */
  steerAngle: number;
  /** Sideways speed, m/s, positive right: the rear sliding. */
  lateral?: number;
  /** Along the heading last step, m/s². */
  accel?: number;
  /** Front wheel up, radians above the ground, and how fast it is rising. */
  wheelie?: number;
  wheelieRate?: number;
  /** The throttle last step, to catch it being snapped open, and how long it has been held. */
  throttleWas?: number;
  throttleHeld?: number;
}

export const UPRIGHT: MotoState = { speed: 0, lean: 0, leanRate: 0, steerAngle: 0 };

export function smoothstep(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

/** A critically damped spring toward a lean, stepped finely enough to be stable. */
function leanToward(state: { lean: number; leanRate: number }, target: number, omega: number, dt: number): void {
  const steps = Math.max(1, Math.ceil(dt / (1 / 120)));
  const h = dt / steps;
  for (let i = 0; i < steps; i++) {
    state.leanRate += (omega * omega * (target - state.lean) - 2 * omega * state.leanRate) * h;
    state.lean += state.leanRate * h;
  }
}

/**
 * Advance the bike on the ground by `dt`. `slope` is the gradient along the
 * heading, positive uphill. Returns the new state and the yaw change,
 * positive clockwise.
 *
 * The bars ask for a turn: at a crawl they give it directly, from the
 * wheelbase and the lock; at speed no tighter than grip allows. The bike
 * leans to where gravity balances that turn — atan(v·ω/g) — and the turn
 * follows the lean, so turning in takes the moment it takes to lay the bike
 * over, and a corner at the limit is at the lean the tyres can hold.
 */
export function stepMotorbike(state: MotoState, input: RideInput, slope: number, dt: number, surface: Surface = MOTO_ROAD, spec: TwoWheelerSpec = MOTO_SPEC): { state: MotoState; yawDelta: number } {
  const RIDE = spec.ride, WHEELIE = spec.wheelie;
  const before = state.speed;
  let speed = state.speed;
  const top = RIDE.maxSpeed * surface.topSpeed;
  const fade = Math.max(0, 1 - (Math.max(0, speed) / top) ** 2);
  const drive = input.throttle > 0 && speed > -0.2 ? RIDE.power * input.throttle * fade : 0;

  // The front wheel: see `WHEELIE`. A machine with no `wheelie` keeps both on the ground.
  let wheelie = state.wheelie ?? 0, wheelieRate = state.wheelieRate ?? 0;
  const held = input.throttle > 0 ? (state.throttleHeld ?? 0) + dt : 0;
  if (WHEELIE !== undefined) {
    const gear = held <= WHEELIE.shiftAfter ? 1 : Math.exp(-(held - WHEELIE.shiftAfter) / WHEELIE.shiftTime);
    const lift = WHEELIE.lift * drive * gear * surface.grip * Math.max(0, 1 - Math.max(0, speed) / WHEELIE.fadeSpeed);
    const weight = WHEELIE.gravity * (1 - wheelie / WHEELIE.balance);
    if (input.throttle > 0 && (state.throttleWas ?? 0) <= 0 && speed > 1 && speed < WHEELIE.kickSpeed) {
      wheelieRate += WHEELIE.kick * input.throttle;
    }
    if (wheelie > 0 || wheelieRate > 0 || lift > weight) {
      const down = weight + (input.throttle <= 0 ? WHEELIE.release : 0) + (input.throttle < 0 || input.slide ? WHEELIE.brake : 0);
      wheelieRate += (lift - down - WHEELIE.damping * wheelieRate) * dt;
      wheelie += wheelieRate * dt;
      if (wheelie <= 0) { wheelie = 0; wheelieRate = 0; }
      if (wheelie >= WHEELIE.max) { wheelie = WHEELIE.max; wheelieRate = Math.min(0, wheelieRate); }
    }
  }
  // On the back wheel the bars steer nothing, and the bike leans less.
  const steering = Math.max(0.3, 1 - wheelie * 2.2);

  if (input.throttle > 0) {
    speed += drive * dt;
  } else if (input.throttle < 0) {
    if (speed > 0.2) speed = Math.max(0, speed - RIDE.brake * dt);
    else speed = Math.max(-RIDE.reverseMax, speed + 2 * input.throttle * dt);
  } else if (Math.abs(speed) > 0.05) {
    speed -= Math.sign(speed) * Math.min(Math.abs(speed), RIDE.engineBrake * dt);
  }
  if (input.slide && Math.abs(speed) > 0.05) {
    speed -= Math.sign(speed) * Math.min(Math.abs(speed), RIDE.slideBrake * dt);
  }

  const gravityAccel = -RIDE.gravity * Math.sin(Math.atan(slope));
  if (Math.abs(speed) <= 0.05 && input.throttle === 0 && Math.abs(gravityAccel) < RIDE.parkingHold) {
    speed = 0;
  } else {
    speed += gravityAccel * dt;
    const resist = (surface.rolling + RIDE.drag * speed * speed) * dt;
    speed -= Math.sign(speed) * Math.min(Math.abs(speed), resist);
  }
  speed = Math.max(-RIDE.reverseMax, Math.min(top * 1.02, speed));

  // The turn the bars ask for, and the lean that holds it.
  const v = Math.abs(speed);
  const grip = RIDE.cornerGrip * surface.grip;
  const bars = (v * Math.tan(RIDE.steerLock)) / spec.geometry.wheelbase;
  const asked = input.steer * steering * Math.min(bars, grip / Math.max(0.5, v)) * (speed < 0 ? -1 : 1);
  const leanTarget = Math.max(-RIDE.maxLean, Math.min(RIDE.maxLean, Math.atan((speed * asked) / RIDE.gravity)));
  const next = { lean: state.lean, leanRate: state.leanRate };
  leanToward(next, leanTarget, RIDE.leanRate, dt);

  // At speed the bike turns as far as it leans; at walking pace, as the bars point.
  const leaned = v > 0.5 ? (RIDE.gravity * Math.tan(next.lean)) / speed : 0;
  const w = smoothstep(1.5, 5, v);
  let yawRate = asked * (1 - w) + leaned * w;
  // The rear brake locked into a corner swings the tail round, like the car's handbrake.
  if (input.slide && v > 4) yawRate *= 1.5;
  const yawDelta = yawRate * dt;

  // The bars: the angle that makes this turn, less a flick of countersteer
  // as the bike is laid over.
  const barsWant = Math.atan((spec.geometry.wheelbase * yawRate) / Math.max(0.8, v)) * (speed < 0 ? -1 : 1) - next.leanRate * 0.05;
  const barsClamped = Math.max(-RIDE.steerLock, Math.min(RIDE.steerLock, barsWant));
  const steerAngle = state.steerAngle + (barsClamped - state.steerAngle) * (1 - Math.exp(-dt * 14));

  // Momentum and grip, as the car's: the knobblies let go sooner in the dirt.
  let lateral = state.lateral ?? 0;
  lateral -= speed * yawDelta;
  lateral *= Math.exp(-(input.slide ? RIDE.slideGrip : RIDE.lateralGrip) * surface.grip * dt);
  speed -= Math.sign(speed) * Math.min(Math.abs(speed), Math.abs(lateral) * 0.3 * dt);
  if (Math.abs(lateral) < 1e-4) lateral = 0;

  return {
    state: {
      speed, lean: next.lean, leanRate: next.leanRate, steerAngle, lateral, accel: dt > 0 ? (speed - before) / dt : 0,
      wheelie, wheelieRate, throttleWas: input.throttle, throttleHeld: held,
    },
    yawDelta,
  };
}

/**
 * The bike in the air: momentum and a little drag, the lean following the
 * rider's whip (A/D lays it sideways), and a slow scrub of a turn.
 */
export function stepMotorbikeAir(state: MotoState, steer: number, dt: number, spec: TwoWheelerSpec = MOTO_SPEC): { state: MotoState; yawDelta: number } {
  const speed = state.speed * (1 - spec.ride.drag * Math.abs(state.speed) * dt);
  const next = { lean: state.lean, leanRate: state.leanRate };
  leanToward(next, steer * 0.6, 6, dt);
  const steerAngle = state.steerAngle + (-steer * 0.15 - state.steerAngle) * (1 - Math.exp(-dt * 6));
  return {
    state: { ...state, speed, lean: next.lean, leanRate: next.leanRate, steerAngle, lateral: 0, accel: 0, wheelie: 0, wheelieRate: 0 },
    yawDelta: steer * 0.45 * dt,
  };
}

/** Where a grip is, in the bike's frame, with the bars turned `steer` (radians, right positive). */
export function gripPoint(side: 1 | -1, steer: number, out = new Vector3()): Vector3 {
  out.set(side * GRIP.x, GRIP.y, GRIP.z);
  out.applyAxisAngle(Y_AXIS, -steer);
  out.applyAxisAngle(X_AXIS, RAKE);
  return out.add(HEAD);
}

const X_AXIS = new Vector3(1, 0, 0);
const Y_AXIS = new Vector3(0, 1, 0);
const DOWN = new Vector3(0, -1, 0);

// --- suspension -----------------------------------------------------------------

/**
 * The sprung chassis over two wheels. The chassis rides its springs toward
 * the ground; each wheel stays on the ground under it; what lies between is
 * how far that end is compressed.
 */
export interface MotoRide {
  /** Chassis height: where the ground would be at static sag. */
  y: number;
  vy: number;
  /** Nose-up, radians: the chassis on its springs. */
  pitch: number;
  vPitch: number;
  /** The front up on the back wheel, radians, on top of `pitch`: the bike pivots on its rear axle. */
  wheelie: number;
  /** Compression, metres: along the fork, and at the rear axle. */
  front: number;
  rear: number;
  started: boolean;
  sinceLanding: number;
}

export function createMotoRide(spec: TwoWheelerSpec = MOTO_SPEC): MotoRide {
  const g = spec.geometry;
  return { y: 0, vy: 0, pitch: 0, vPitch: 0, wheelie: 0, front: g.frontSag, rear: g.rearSag, started: false, sinceLanding: 99 };
}

export interface MotoRideTarget {
  /** Where the bike is: the ground under its middle, or its height in the air. */
  y: number;
  /** The ground under each axle. */
  groundFront: number;
  groundRear: number;
  /** Along the heading, m/s²: braking dives, power squats. */
  accel: number;
  /** The bike's pitch in the air, radians nose-up. */
  airPitch: number;
  /** On the back wheel, radians. */
  wheelie?: number;
}

export function updateMotoRide(ride: MotoRide, t: MotoRideTarget, dt: number, airborne = false, landing = 0, spec: TwoWheelerSpec = MOTO_SPEC): MotoRide {
  const MOTO = spec.geometry, RAKE = MOTO.rake;
  const front = -MOTO.frontAxleZ, rear = MOTO.rearAxleZ;
  const groundPitch = Math.atan((t.groundFront - t.groundRear) / (front + rear));
  if (!ride.started || (!airborne && Math.abs(t.y - ride.y) > 5)) {
    Object.assign(ride, { y: t.y, vy: 0, pitch: groundPitch, vPitch: 0, front: MOTO.frontSag, rear: MOTO.rearSag, started: true });
  }
  ride.wheelie = airborne ? 0 : t.wheelie ?? 0;
  if (airborne) {
    // Unloaded, the springs push the wheels to the ends of their travel.
    ride.y = t.y;
    ride.vy = 0;
    ride.vPitch = 0;
    ride.pitch += (t.airPitch - ride.pitch) * (1 - Math.exp(-dt * 12));
    const k = Math.exp(-dt * 14);
    ride.front *= k;
    ride.rear *= k;
    return ride;
  }
  ride.sinceLanding += dt;
  if (landing < -0.5) {
    // The chassis carries on down into the springs, the harder the deeper.
    ride.vy = Math.max(-7.5, landing * 0.9);
    ride.sinceLanding = 0;
  }
  // The squat and dive, as far as the travel allows: a scooter's few centimetres pitch it less.
  const reach = Math.min(1, (MOTO.forkTravel + MOTO.rearTravel) / 0.61);
  const squat = Math.max(-0.075, Math.min(0.06, t.accel * 0.009)) * reach;
  const pitchTarget = groundPitch + squat;
  // Soft, and underdamped just after a landing: a bike bobs on its springs.
  const settle = Math.min(1, ride.sinceLanding / 0.9);
  const zeta = 0.3 + 0.35 * settle;
  const w = spec.heave, wp = spec.pitchRate;
  const steps = Math.max(1, Math.ceil(dt / (1 / 120)));
  const h = dt / steps;
  for (let i = 0; i < steps; i++) {
    ride.vy += (w * w * (t.y - ride.y) - 2 * zeta * w * ride.vy) * h;
    ride.y += ride.vy * h;
    ride.vPitch += (wp * wp * (pitchTarget - ride.pitch) - 2 * 0.6 * wp * ride.vPitch) * h;
    ride.pitch += ride.vPitch * h;
  }
  // Up on the back wheel the whole bike turns about the rear axle: the
  // middle rises with it and the front wheel leaves the ground.
  const lift = rear * Math.sin(ride.wheelie);
  const compressions = () => {
    const tp = Math.tan(ride.pitch + ride.wheelie);
    const y = ride.y + lift;
    return [
      MOTO.frontSag + (t.groundFront - (y + front * tp)) / Math.cos(RAKE),
      MOTO.rearSag + (t.groundRear - (y - rear * tp)),
    ];
  };
  let [cf, cr] = compressions();
  // Bottomed out: the chassis sits on its stops, and rises with the ground.
  const over = Math.max(0, (cf - MOTO.forkTravel) * Math.cos(RAKE), cr - MOTO.rearTravel);
  if (over > 0) {
    ride.y += over;
    ride.vy = Math.max(0, ride.vy);
    [cf, cr] = compressions();
  }
  ride.y = Math.min(t.y + Math.max(MOTO.forkTravel, MOTO.rearTravel) * 1.15, ride.y);
  ride.front = Math.max(0, Math.min(MOTO.forkTravel, cf));
  ride.rear = Math.max(0, Math.min(MOTO.rearTravel, cr));
  return ride;
}

// --- materials ------------------------------------------------------------------

/** The player's colours: shirt orange, trouser navy, shoe white. */
const ORANGE = 0xf0772f;
const NAVY = 0x2e4670;
const WHITE = 0xf3f0e8;

import { applyStreetLampLight } from './terrainMaterial.js';

export const faceted = (color: number, roughness: number, metalness = 0, extra: Partial<MeshStandardMaterial> = {}) => {
  const m = new MeshStandardMaterial({ color: new Color(color), roughness, metalness, flatShading: true });
  Object.assign(m, extra);
  if (extra.transparent !== true) applyStreetLampLight(m);
  return m;
};

const plastic = faceted(ORANGE, 0.42, 0.05);
/** The bodywork's colour: a parked bike takes its own, and so does the one ridden from it. */
export const motoPaint = plastic;
const plasticWhite = faceted(WHITE, 0.45, 0.05);
/** The white plastics: number boards, shrouds' trim. Like the coloured ones, they crack off in a crash. */
export const motoWhite = plasticWhite;
const graphic = faceted(NAVY, 0.5, 0.05);
const seatCover = faceted(0x27395c, 0.85);
const frameMat = faceted(0x2f4371, 0.42, 0.25);
export const alu = faceted(0xc9ccd1, 0.38, 0.35);
export const darkAlu = faceted(0x4a4f58, 0.5, 0.3);
const anodized = faceted(0xf07a2a, 0.38, 0.3);
const gold = faceted(0xe0b252, 0.28, 0.45);
const titanium = faceted(0xc2a57a, 0.34, 0.45);
export const chrome = faceted(0xdfe2e6, 0.25, 0.5);
export const steel = faceted(0xadb1b8, 0.38, 0.45);
export const black = faceted(0x1d1e22, 0.65, 0.1);
export const rubber = faceted(0x1c1c1f, 0.95);
export const rimBlack = faceted(0x26282d, 0.4, 0.3);
/** The bike's lamps: exported so the traffic can find them in the model and draw its own there (see `carLights.ts`). */
export const headlamp = faceted(0xfff4df, 0.2, 0.1, { emissive: new Color(0xfff0c8), emissiveIntensity: 0.4 });
export const taillamp = faceted(0xb3161b, 0.3, 0.1, { emissive: new Color(0xff2a1a), emissiveIntensity: 0.5 });

// --- building blocks ------------------------------------------------------------

const m4 = new Matrix4();
const q4 = new Quaternion();
const e4 = new Euler();
const v4 = new Vector3();
const ONE = new Vector3(1, 1, 1);

/** Move and turn a geometry into place (Euler XYZ). */
export function placed(g: BufferGeometry, x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0): BufferGeometry {
  e4.set(rx, ry, rz);
  return g.applyMatrix4(m4.compose(v4.set(x, y, z), q4.setFromEuler(e4), ONE));
}

export type V3 = readonly [number, number, number];
const vec = (p: V3) => new Vector3(p[0], p[1], p[2]);

/** A cylinder from `a` to `b` — radius `r` at `a`, `r2` at `b`. */
export function rod(a: V3, b: V3, r: number, sides = 6, r2 = r): BufferGeometry {
  const from = vec(a), to = vec(b);
  const dir = to.clone().sub(from);
  const g = new CylinderGeometry(r2, r, dir.length(), sides);
  q4.setFromUnitVectors(Y_AXIS, dir.normalize());
  return g.applyMatrix4(m4.compose(from.add(to).multiplyScalar(0.5), q4, ONE));
}

/** A box from `a` to `b`, `w` across X and `d` in depth. */
export function beam(a: V3, b: V3, w: number, d: number): BufferGeometry {
  const from = vec(a), to = vec(b);
  const dir = to.clone().sub(from);
  const g = new BoxGeometry(w, dir.length(), d);
  q4.setFromUnitVectors(Y_AXIS, dir.normalize());
  return g.applyMatrix4(m4.compose(from.add(to).multiplyScalar(0.5), q4, ONE));
}

/** A bent tube through points: frame rails, the exhaust header, hoses. */
export function tube(points: V3[], r: number, sides = 6): BufferGeometry {
  return new TubeGeometry(new CatmullRomCurve3(points.map(vec)), points.length * 7, r, sides, false);
}

/** A side profile (z, y), extruded `width` across the bike and centred at `x`. */
export function slab(points: Array<[number, number]>, width: number, x = 0, bevel = 0): BufferGeometry {
  const shape = new Shape();
  shape.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) shape.lineTo(points[i][0], points[i][1]);
  shape.closePath();
  const depth = Math.max(0.002, width - 2 * bevel);
  const g = new ExtrudeGeometry(shape, {
    depth, bevelEnabled: bevel > 0, bevelThickness: bevel, bevelSize: bevel, bevelSegments: 1, curveSegments: 6,
  });
  g.translate(0, 0, -depth / 2);
  g.rotateY(-Math.PI / 2);
  return g.translate(x, 0, 0);
}

/** Seven-segment strokes for a race number, in the XY plane, facing +Z. */
function digit(n: number, height: number, depth: number): BufferGeometry {
  const w = height * 0.55, t = height * 0.17;
  const on = ['abcdef', 'bc', 'abged', 'abgcd', 'fgbc', 'afgcd', 'afgedc', 'abc', 'abcdefg', 'abcdfg'][n];
  const seg: Record<string, [number, number, number, number]> = {
    a: [0, height / 2 - t / 2, w, t], d: [0, -height / 2 + t / 2, w, t], g: [0, 0, w, t],
    b: [w / 2 - t / 2, height / 4, t, height / 2], c: [w / 2 - t / 2, -height / 4, t, height / 2],
    f: [-w / 2 + t / 2, height / 4, t, height / 2], e: [-w / 2 + t / 2, -height / 4, t, height / 2],
  };
  return mergeGeometries([...on].map((s) => {
    const [x, y, sw, sh] = seg[s];
    return new BoxGeometry(sw, sh, depth).translate(x, y, 0).toNonIndexed();
  }));
}

/**
 * Parts collected by material and merged: one draw call per material per
 * moving piece, however many bolts it has.
 */
export class Kit {
  private readonly parts = new Map<Material, BufferGeometry[]>();

  add(material: Material, ...geometries: BufferGeometry[]): void {
    let list = this.parts.get(material);
    if (list === undefined) this.parts.set(material, list = []);
    for (const geometry of geometries) {
      const g = geometry.index === null ? geometry : geometry.toNonIndexed();
      for (const name of Object.keys(g.attributes)) if (name !== 'position') g.deleteAttribute(name);
      g.computeVertexNormals();
      list.push(g);
    }
  }

  into(group: Group): Group {
    for (const [material, list] of this.parts) {
      const mesh = new Mesh(mergeGeometries(list), material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      group.add(mesh);
    }
    return group;
  }
}

/**
 * A wheel: knobbly tyre, anodised rim, crossed spokes, hub, and the brake
 * disc (and on the rear the sprocket) that turn with it. Spins about X.
 */
function buildWheel(r: number, w: number, rimR: number, disc: { side: 1 | -1; radius: number }, sprocket?: number): Group {
  const kit = new Kit();
  const knob = 0.017;
  const c = r - knob;
  // The carcass, turned in section.
  const wall = rimR + 0.4 * (c - rimR);
  const section = [
    new Vector2(rimR, -w * 0.4), new Vector2(wall, -w / 2), new Vector2(c - 0.014, -w * 0.46),
    new Vector2(c, -w * 0.24), new Vector2(c, w * 0.24), new Vector2(c - 0.014, w * 0.46),
    new Vector2(wall, w / 2), new Vector2(rimR, w * 0.4),
  ];
  kit.add(rubber, new LatheGeometry(section, 28).rotateZ(Math.PI / 2));
  // Knobs: pairs and singles staggered down the middle, and a row on each
  // shoulder, canted out — the tread that reads as dirt from ten metres.
  const n = Math.round((Math.PI * 2 * c) / 0.058);
  const knobs: BufferGeometry[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    if (i % 2 === 0) {
      for (const s of [-1, 1]) knobs.push(new BoxGeometry(w * 0.26, knob, 0.028).translate(s * w * 0.17, c + knob / 2, 0).rotateX(a));
    } else {
      knobs.push(new BoxGeometry(w * 0.3, knob, 0.03).translate(0, c + knob / 2, 0).rotateX(a));
    }
    for (const s of [-1, 1]) {
      knobs.push(new BoxGeometry(w * 0.2, knob * 0.9, 0.026).rotateZ(-s * 0.6)
        .translate(s * w * 0.42, c - 0.009 + knob / 2, 0).rotateX(a + Math.PI / n));
    }
  }
  kit.add(rubber, ...knobs);
  // The rim: a channel, anodised black, with its lips.
  kit.add(rimBlack, new CylinderGeometry(rimR, rimR, w * 0.82, 28, 1, true).rotateZ(Math.PI / 2));
  for (const s of [-1, 1]) kit.add(rimBlack, new CylinderGeometry(rimR + 0.008, rimR + 0.008, 0.008, 28, 1, true).rotateZ(Math.PI / 2).translate(s * w * 0.41, 0, 0));
  kit.add(steel, new CylinderGeometry(rimR - 0.012, rimR - 0.012, w * 0.5, 28, 1, true).rotateZ(Math.PI / 2));
  // Thirty-six spokes, laced from alternate hub flanges and crossed.
  const spokes: BufferGeometry[] = [];
  for (let k = 0; k < 36; k++) {
    const s = k % 2 === 0 ? 1 : -1;
    const a = (k / 36) * Math.PI * 2;
    const b = a + (k % 4 < 2 ? 0.42 : -0.42);
    spokes.push(rod(
      [s * 0.036, 0.05 * Math.cos(a), 0.05 * Math.sin(a)],
      [s * 0.01, (rimR - 0.012) * Math.cos(b), (rimR - 0.012) * Math.sin(b)], 0.0032, 4));
  }
  kit.add(chrome, ...spokes);
  // Hub and flanges, and the axle nut.
  kit.add(anodized, new CylinderGeometry(0.036, 0.036, 0.1, 12).rotateZ(Math.PI / 2));
  for (const s of [-1, 1]) kit.add(anodized, new CylinderGeometry(0.058, 0.058, 0.008, 14).rotateZ(Math.PI / 2).translate(s * 0.036, 0, 0));
  kit.add(chrome, new CylinderGeometry(0.014, 0.014, 0.16, 6).rotateZ(Math.PI / 2));
  // The wavy disc on its carrier.
  const outer = new Shape();
  for (let i = 0; i <= 96; i++) {
    const a = (i / 96) * Math.PI * 2;
    const rr = disc.radius * (1 + 0.04 * Math.cos(a * 12));
    if (i === 0) outer.moveTo(rr * Math.cos(a), rr * Math.sin(a));
    else outer.lineTo(rr * Math.cos(a), rr * Math.sin(a));
  }
  outer.holes.push(new Path().absarc(0, 0, disc.radius * 0.7, 0, Math.PI * 2, true));
  for (let i = 0; i < 12; i++) {
    const a = ((i + 0.5) / 12) * Math.PI * 2;
    outer.holes.push(new Path().absarc(disc.radius * 0.84 * Math.cos(a), disc.radius * 0.84 * Math.sin(a), 0.006, 0, Math.PI * 2, true));
  }
  const dx = disc.side * 0.062;
  kit.add(steel, new ExtrudeGeometry(outer, { depth: 0.004, bevelEnabled: false, curveSegments: 4 }).rotateY(Math.PI / 2).translate(dx - 0.002, 0, 0));
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    kit.add(darkAlu, beam([dx, 0.03 * Math.cos(a), 0.03 * Math.sin(a)], [dx, disc.radius * 0.74 * Math.cos(a), disc.radius * 0.74 * Math.sin(a)], 0.008, 0.014));
  }
  if (sprocket !== undefined) {
    // The rear sprocket, 49 teeth and lightened, on the other side.
    const gear = new Shape();
    const teeth = 49;
    for (let i = 0; i <= teeth * 4; i++) {
      const a = (i / (teeth * 4)) * Math.PI * 2;
      const rr = i % 4 < 2 ? sprocket : sprocket - 0.01;
      if (i === 0) gear.moveTo(rr * Math.cos(a), rr * Math.sin(a));
      else gear.lineTo(rr * Math.cos(a), rr * Math.sin(a));
    }
    gear.holes.push(new Path().absarc(0, 0, 0.042, 0, Math.PI * 2, true));
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      gear.holes.push(new Path().absarc(sprocket * 0.62 * Math.cos(a), sprocket * 0.62 * Math.sin(a), sprocket * 0.2, 0, Math.PI * 2, true));
    }
    kit.add(anodized, new ExtrudeGeometry(gear, { depth: 0.006, bevelEnabled: false, curveSegments: 5 }).rotateY(Math.PI / 2).translate(-disc.side * 0.066, 0, 0));
  }
  return kit.into(new Group());
}

/**
 * A parked bike: the ridden one's outline — its wheels, fork, engine, tank,
 * seat, fenders and silencer where they are on it — in some 310 triangles
 * to its 19,000. No spokes, chain, sprocket, hoses, fins or controls: from
 * the pavement it reads as the same bike, and a street of them costs less
 * than one of the ridden. The plastics are `motoPaint`, for an instance
 * colour to tint.
 */
export function createParkedMotorbike(): Group {
  const kit = new Kit();
  for (const [z, r, w] of [[MOTO.frontAxleZ, MOTO.frontRadius, MOTO.frontTyre], [MOTO.rearAxleZ, MOTO.rearRadius, MOTO.rearTyre]]) {
    kit.add(rubber, new CylinderGeometry(r, r, w, 10, 1, true).rotateZ(Math.PI / 2).translate(0, r, z));
    for (const s of [-1, 1]) {
      kit.add(rubber, new CircleGeometry(r, 10).rotateY(s * Math.PI / 2).translate(s * w / 2, r, z));
      kit.add(alu, new CircleGeometry(r * 0.72, 10).rotateY(s * Math.PI / 2).translate(s * (w / 2 + 0.003), r, z));
    }
  }
  const top: V3 = [0, HEAD.y + Math.cos(RAKE) * 0.1, HEAD.z + Math.sin(RAKE) * 0.1];
  kit.add(darkAlu, beam(top, [0, MOTO.frontRadius, MOTO.frontAxleZ], 0.2, 0.05));
  kit.add(alu, beam([-0.42, top[1] + 0.07, top[2] + 0.02], [0.42, top[1] + 0.07, top[2] + 0.02], 0.025, 0.025));
  kit.add(plastic, beam([0, 2 * MOTO.frontRadius + 0.05, MOTO.frontAxleZ - 0.3], [0, 2 * MOTO.frontRadius + 0.07, MOTO.frontAxleZ + 0.15], 0.14, 0.02));
  kit.add(darkAlu, slab([[-0.34, 0.44], [-0.3, 0.56], [-0.16, 0.62], [0.04, 0.63], [0.11, 0.54], [0.09, 0.38], [-0.08, 0.35], [-0.27, 0.36]], 0.19));
  for (const s of [-1, 1]) kit.add(darkAlu, beam([s * 0.08, PIVOT.y, PIVOT.z], [s * 0.08, MOTO.rearRadius, MOTO.rearAxleZ], 0.03, 0.05));
  // The tank and shrouds as one, the white side panels, the seat, the rear fender.
  kit.add(plastic, slab([[-0.43, 0.84], [-0.38, 0.99], [-0.16, 1.03], [0.04, 0.97], [0.08, 0.88], [0, 0.64], [-0.18, 0.6], [-0.4, 0.66]], 0.33));
  kit.add(plasticWhite, slab([[0.2, 0.87], [0.7, 0.9], [0.74, 0.84], [0.52, 0.63], [0.26, 0.67]], 0.29));
  kit.add(seatCover, slab([[-0.2, 0.935], [0.3, 0.95], [0.84, 0.922], [0.84, 0.89], [-0.05, 0.885], [-0.2, 0.915]], 0.26));
  kit.add(plastic, slab([[0.32, 0.895], [0.84, 0.915], [1.13, 0.975], [1.15, 0.96], [0.86, 0.892], [0.36, 0.872]], 0.21));
  kit.add(alu, rod([0.2, 0.78, 0.43], [0.19, 0.885, 0.92], 0.052, 6));
  return kit.into(new Group());
}

// --- the model --------------------------------------------------------------------

export interface MotoModel {
  kind: 'moto';
  root: Group;
  /** Turns with the bars, about the raked steering axis. */
  steer: Group;
  /** The fork's sliding half, with the front wheel: moves along the fork. */
  forkLower: Group;
  frontWheel: Group;
  rearWheel: Group;
  swingarm: Group;
  shock: Group;
  shockRod: Group;
  shockSpring: Mesh;
  headlight: SpotLight;
}

/**
 * Build the bike. The root's origin is on the ground midway between the
 * axles, so placing it is "put it where the ground is"; its parts are placed
 * at static sag and moved by `animateMotorbike`.
 */
export function createMotorbike(): MotoModel {
  const root = new Group();
  const chassis = new Kit();

  // --- frame: steering head, twin spars, the cradle under the engine ---------
  const axis = new Vector3(0, Math.cos(RAKE), Math.sin(RAKE));
  const at = (along: number): V3 => [HEAD.x + axis.x * along, HEAD.y + axis.y * along, HEAD.z + axis.z * along];
  chassis.add(frameMat, rod(at(-0.13), at(0.09), 0.033, 10));
  chassis.add(frameMat, tube([[0, 0.97, -0.47], [0, 0.8, -0.46], [0, 0.64, -0.42]], 0.024));
  for (const s of [-1, 1]) {
    chassis.add(frameMat,
      tube([[s * 0.035, 1.08, -0.39], [s * 0.1, 1.0, -0.26], [s * 0.125, 0.88, -0.02], [s * 0.115, 0.7, 0.1], [s * 0.105, 0.47, 0.13]], 0.022),
      tube([[0, 0.64, -0.42], [s * 0.045, 0.49, -0.37], [s * 0.06, 0.37, -0.26], [s * 0.065, 0.345, -0.05], [s * 0.08, 0.39, 0.08], [s * 0.1, 0.46, 0.13]], 0.018),
      new BoxGeometry(0.03, 0.15, 0.11).translate(s * 0.105, 0.52, 0.12),
      // Footpeg bracket.
      new BoxGeometry(0.07, 0.05, 0.05).translate(s * 0.14, 0.42, 0.15));
    // The aluminium subframe carrying the seat and the silencer.
    chassis.add(alu,
      tube([[s * 0.1, 0.88, 0], [s * 0.085, 0.9, 0.4], [s * 0.075, 0.88, 0.7]], 0.014),
      tube([[s * 0.105, 0.64, 0.14], [s * 0.09, 0.76, 0.4], [s * 0.08, 0.87, 0.6]], 0.012));
  }
  chassis.add(chrome, new CylinderGeometry(0.018, 0.018, 0.26, 8).rotateZ(Math.PI / 2).translate(PIVOT.x, PIVOT.y, PIVOT.z));

  // --- engine: cases, cylinder, covers, skid plate --------------------------
  chassis.add(darkAlu, slab([[-0.34, 0.44], [-0.3, 0.56], [-0.16, 0.62], [0.04, 0.63], [0.11, 0.54], [0.09, 0.38], [-0.08, 0.35], [-0.27, 0.36]], 0.19, 0, 0.012));
  chassis.add(darkAlu, placed(new BoxGeometry(0.12, 0.19, 0.13), 0, 0.69, -0.17, -0.25));
  chassis.add(alu, placed(new BoxGeometry(0.15, 0.075, 0.16), 0, 0.8, -0.2, -0.25));
  chassis.add(black, placed(new BoxGeometry(0.13, 0.03, 0.14), 0, 0.845, -0.21, -0.25));
  // Cooling fins cast into the cylinder's flanks, for texture.
  for (let i = 0; i < 4; i++) chassis.add(darkAlu, placed(new BoxGeometry(0.135, 0.012, 0.118), 0, 0.62 + i * 0.04, -0.15 - i * 0.01, -0.25));
  chassis.add(alu, new CylinderGeometry(0.085, 0.09, 0.035, 14).rotateZ(Math.PI / 2).translate(0.1, 0.45, -0.05));
  chassis.add(darkAlu, new CylinderGeometry(0.05, 0.05, 0.01, 12).rotateZ(Math.PI / 2).translate(0.121, 0.45, -0.05));
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    chassis.add(chrome, new CylinderGeometry(0.006, 0.006, 0.01, 6).rotateZ(Math.PI / 2).translate(0.12, 0.45 + 0.074 * Math.cos(a), -0.05 + 0.074 * Math.sin(a)));
  }
  chassis.add(darkAlu, new CylinderGeometry(0.075, 0.08, 0.035, 14).rotateZ(Math.PI / 2).translate(-0.1, 0.48, -0.12));
  chassis.add(anodized, new CylinderGeometry(0.018, 0.018, 0.016, 8).rotateZ(Math.PI / 2).translate(-0.122, 0.48, -0.12));
  chassis.add(black, new BoxGeometry(0.03, 0.08, 0.1).translate(-0.1, 0.48, 0.05));
  chassis.add(anodized, new CylinderGeometry(0.035, 0.035, 0.012, 12).rotateZ(Math.PI / 2).translate(-0.075, 0.47, 0.06));
  chassis.add(black, new BoxGeometry(0.17, 0.012, 0.44).translate(0, 0.327, -0.12), placed(new BoxGeometry(0.17, 0.012, 0.12), 0, 0.37, -0.37, -0.8));
  // Airbox behind it, under the seat.
  chassis.add(black, new BoxGeometry(0.2, 0.2, 0.3).translate(0, 0.78, 0.39));

  // --- radiators, hoses ------------------------------------------------------
  for (const s of [-1, 1]) {
    chassis.add(darkAlu, new BoxGeometry(0.035, 0.25, 0.17).translate(s * 0.13, 0.75, -0.28));
    for (let i = 0; i < 6; i++) chassis.add(black, new BoxGeometry(0.004, 0.23, 0.008).translate(s * 0.149, 0.75, -0.35 + i * 0.028));
    chassis.add(black, tube([[s * 0.12, 0.66, -0.22], [s * 0.07, 0.7, -0.2], [0.0, 0.79, -0.2]], 0.012));
  }

  // --- bodywork: tank, shrouds, seat, side panels, rear fender --------------
  chassis.add(plasticWhite, slab([[-0.37, 0.95], [-0.18, 1.0], [0.02, 0.97], [0.05, 0.9], [-0.05, 0.83], [-0.33, 0.83]], 0.24, 0, 0.012));
  for (const s of [-1, 1] as const) {
    const x = s * 0.165;
    chassis.add(plastic, placed(slab([[-0.43, 0.84], [-0.38, 0.99], [-0.16, 1.03], [0.04, 0.97], [0.08, 0.88], [0, 0.64], [-0.18, 0.6], [-0.4, 0.66]], 0.018), x, 0, 0, 0, s * 0.1));
    chassis.add(graphic, placed(slab([[-0.37, 0.94], [-0.12, 0.98], [0.02, 0.92], [-0.02, 0.87], [-0.33, 0.88]], 0.006), x + s * 0.012, 0, 0, 0, s * 0.1));
    chassis.add(plasticWhite, placed(slab([[-0.36, 0.84], [-0.08, 0.86], [-0.06, 0.82], [-0.34, 0.8]], 0.006), x + s * 0.012, 0, 0, 0, s * 0.1));
    // Side panel, white, with the number on it.
    chassis.add(plasticWhite, slab([[0.2, 0.87], [0.7, 0.9], [0.74, 0.84], [0.52, 0.63], [0.26, 0.67]], 0.014, s * 0.14));
    chassis.add(graphic, placed(digit(7, 0.15, 0.006), s * 0.149, 0.77, 0.46, 0, s * Math.PI / 2));
    // Footpeg, with its teeth.
    chassis.add(alu, new BoxGeometry(0.1, 0.02, 0.055).translate(s * PEG.x, PEG.y, PEG.z));
    for (const dz of [-0.018, 0, 0.018]) chassis.add(darkAlu, new BoxGeometry(0.1, 0.008, 0.006).translate(s * PEG.x, PEG.y + 0.013, PEG.z + dz));
  }
  chassis.add(seatCover, slab([[-0.2, 0.935], [-0.08, 0.96], [0.3, 0.943], [0.72, 0.935], [0.84, 0.922], [0.84, 0.89], [0.4, 0.875], [-0.05, 0.89], [-0.2, 0.915]], 0.26, 0, 0.015));
  // Gripper ribs across the seat.
  for (let i = 0; i < 5; i++) chassis.add(graphic, new BoxGeometry(0.2, 0.006, 0.014).translate(0, 0.978 - i * 0.004, -0.02 + i * 0.07));
  chassis.add(plastic, slab([[0.32, 0.895], [0.84, 0.915], [1.13, 0.975], [1.15, 0.96], [0.86, 0.892], [0.36, 0.872]], 0.21, 0, 0.008));
  chassis.add(taillamp, new BoxGeometry(0.07, 0.022, 0.03).translate(0, 0.952, 1.08));

  // --- controls at the pegs: brake pedal right, gear lever left --------------
  chassis.add(alu, beam([0.14, 0.44, 0.12], [0.17, 0.405, -0.07], 0.012, 0.018), new BoxGeometry(0.05, 0.012, 0.03).translate(0.18, 0.405, -0.085));
  chassis.add(alu, beam([-0.12, 0.47, 0.02], [-0.17, 0.46, -0.1], 0.012, 0.016), new CylinderGeometry(0.01, 0.01, 0.05, 6).rotateZ(Math.PI / 2).translate(-0.18, 0.46, -0.105));

  // --- exhaust: header round the right, high silencer --------------------------
  chassis.add(titanium, tube([[0.02, 0.76, -0.27], [0.08, 0.7, -0.35], [0.13, 0.56, -0.33], [0.155, 0.48, -0.15], [0.14, 0.54, 0.1], [0.17, 0.7, 0.32], [0.2, 0.78, 0.44]], 0.023, 8));
  chassis.add(alu, rod([0.2, 0.78, 0.43], [0.19, 0.885, 0.92], 0.052, 10));
  chassis.add(black, rod([0.19, 0.885, 0.915], [0.189, 0.888, 0.945], 0.055, 10, 0.05), beam([0.2, 0.83, 0.62], [0.2, 0.84, 0.66], 0.11, 0.11));
  chassis.add(chrome, rod([0.189, 0.888, 0.94], [0.188, 0.89, 0.965], 0.018, 8));

  chassis.into(root);

  // --- the steering: tilted along the rake, turning about it ------------------
  const tilt = new Group();
  tilt.position.copy(HEAD);
  tilt.rotation.x = RAKE;
  const steer = new Group();
  tilt.add(steer);
  root.add(tilt);
  const bars = new Kit();
  for (const s of [-1, 1] as const) {
    // Fork outer tubes in the clamps, with their seals and caps.
    bars.add(darkAlu, rod([s * 0.09, -0.46, 0], [s * 0.09, 0.12, 0], 0.031, 10));
    bars.add(black, rod([s * 0.09, -0.475, 0], [s * 0.09, -0.455, 0], 0.034, 10));
    bars.add(anodized, rod([s * 0.09, 0.12, 0], [s * 0.09, 0.14, 0], 0.028, 10));
    bars.add(anodized, new CylinderGeometry(0.043, 0.043, 0.03, 10).translate(s * 0.09, 0.09, 0), new CylinderGeometry(0.044, 0.044, 0.045, 10).translate(s * 0.09, -0.13, 0));
    // Bar mounts, the bar, grips with their ribs, levers.
    bars.add(black, new BoxGeometry(0.03, 0.045, 0.035).translate(s * 0.05, 0.12, 0.012));
    bars.add(black, rod([s * 0.3, GRIP.y, GRIP.z], [s * 0.42, GRIP.y, GRIP.z], 0.018, 8));
    for (let i = 0; i < 4; i++) bars.add(darkAlu, rod([s * (0.32 + i * 0.025), GRIP.y, GRIP.z], [s * (0.326 + i * 0.025), GRIP.y, GRIP.z], 0.0195, 8));
    bars.add(alu, beam([s * 0.26, GRIP.y + 0.005, GRIP.z - 0.02], [s * 0.4, GRIP.y - 0.01, GRIP.z - 0.09], 0.012, 0.008));
    bars.add(black, new BoxGeometry(0.035, 0.03, 0.035).translate(s * 0.26, GRIP.y, GRIP.z - 0.01));
  }
  bars.add(anodized, new BoxGeometry(0.26, 0.024, 0.075).translate(0, 0.09, 0), new BoxGeometry(0.24, 0.035, 0.085).translate(0, -0.13, 0));
  bars.add(chrome, new CylinderGeometry(0.016, 0.016, 0.02, 8).translate(0, 0.105, 0));
  bars.add(alu, tube([[-0.42, GRIP.y, GRIP.z], [-0.3, GRIP.y, GRIP.z], [-0.16, 0.142, 0.01], [0, 0.14, 0.01], [0.16, 0.142, 0.01], [0.3, GRIP.y, GRIP.z], [0.42, GRIP.y, GRIP.z]], 0.012, 8));
  bars.add(graphic, new BoxGeometry(0.2, 0.034, 0.034).translate(0, 0.17, 0.01));
  bars.add(black, new BoxGeometry(0.04, 0.028, 0.03).translate(0.22, 0.175, 0.03));
  // Number plate in front of the forks, white with the navy number, and a small lamp.
  const plate = new Shape();
  plate.moveTo(-0.13, -0.12);
  plate.lineTo(0.13, -0.12);
  plate.lineTo(0.15, 0.08);
  plate.quadraticCurveTo(0, 0.14, -0.15, 0.08);
  plate.closePath();
  bars.add(plasticWhite, new ExtrudeGeometry(plate, { depth: 0.01, bevelEnabled: true, bevelSize: 0.006, bevelThickness: 0.004, bevelSegments: 1 }).translate(0, 0.0, -0.085));
  bars.add(graphic, digit(7, 0.12, 0.006).rotateY(Math.PI).translate(0, 0.025, -0.09));
  bars.add(headlamp, new BoxGeometry(0.1, 0.035, 0.012).translate(0, -0.075, -0.093));
  bars.into(steer);

  // The front fender, under the lower clamp, is drawn upright.
  const upright = new Group();
  upright.rotation.x = -RAKE;
  steer.add(upright);
  const fenderKit = new Kit();
  const frontAxle = new Vector3(0, -FORK_AT_SAG, 0).applyAxisAngle(X_AXIS, RAKE);
  const arc = (radius: number, a0: number, a1: number, n: number) =>
    Array.from({ length: n + 1 }, (_, i): [number, number] => {
      const a = a0 + ((a1 - a0) * i) / n;
      return [frontAxle.z - radius * Math.sin(a), frontAxle.y + radius * Math.cos(a)];
    });
  fenderKit.add(plastic, slab([...arc(0.5, -0.45, 1.05, 8), ...arc(0.486, 1.05, -0.45, 8)], 0.115, 0, 0.005));
  // Its mount, up to the lower clamp.
  fenderKit.add(black, new BoxGeometry(0.08, 0.05, 0.08).translate(0, frontAxle.y + 0.52, frontAxle.z + 0.02));
  fenderKit.into(upright);

  const headlight = new SpotLight(0xfff0d0, 0, 60, 0.45, 0.6, 1.4);
  headlight.position.set(0, -0.075, -0.1);
  // In the tilted frame: this is level ahead, dipping a little over 20 m.
  headlight.target.position.set(0, -10, -18);
  steer.add(headlight, headlight.target);

  // --- the fork's lower half: inner tubes, guards, lugs, caliper, wheel ------
  const forkLower = new Group();
  forkLower.position.y = -FORK_AT_SAG;
  steer.add(forkLower);
  const lower = new Kit();
  for (const s of [-1, 1] as const) {
    lower.add(gold, rod([s * 0.09, 0.05, 0], [s * 0.09, 0.44, 0], 0.024, 10));
    lower.add(darkAlu, new BoxGeometry(0.05, 0.1, 0.07).translate(s * 0.09, 0.03, 0.0));
    lower.add(plastic, new BoxGeometry(0.058, 0.26, 0.014).translate(s * 0.09, 0.2, -0.036));
    lower.add(plastic, new BoxGeometry(0.012, 0.26, 0.05).translate(s * 0.12, 0.2, -0.014));
    lower.add(chrome, new CylinderGeometry(0.016, 0.016, 0.012, 6).rotateZ(Math.PI / 2).translate(s * 0.12, 0, 0));
  }
  lower.into(forkLower);
  const lowerUpright = new Group();
  lowerUpright.rotation.x = -RAKE;
  forkLower.add(lowerUpright);
  const caliper = new Kit();
  caliper.add(darkAlu, new BoxGeometry(0.04, 0.07, 0.055).translate(-0.066, 0.075, 0.1));
  caliper.add(darkAlu, beam([-0.09, 0.02, 0.02], [-0.07, 0.07, 0.09], 0.012, 0.03));
  caliper.into(lowerUpright);
  const frontWheel = buildWheel(MOTO.frontRadius, MOTO.frontTyre, 0.268, { side: -1, radius: 0.13 });
  lowerUpright.add(frontWheel);

  // --- swingarm, chain, rear wheel ---------------------------------------------
  const swingarm = new Group();
  swingarm.position.copy(PIVOT);
  root.add(swingarm);
  const arm = new Kit();
  for (const s of [-1, 1] as const) {
    arm.add(alu, beam([s * 0.085, 0, 0.02], [s * 0.09, ARM.y, ARM.z], 0.03, 0.06));
    arm.add(alu, new BoxGeometry(0.03, 0.035, 0.05).translate(s * 0.09, ARM.y, ARM.z + 0.02));
  }
  arm.add(alu, new CylinderGeometry(0.03, 0.03, 0.2, 10).rotateZ(Math.PI / 2), new BoxGeometry(0.15, 0.05, 0.06).translate(0, -0.03, 0.12));
  arm.add(darkAlu, new BoxGeometry(0.05, 0.04, 0.05).translate(SHOCK_FOOT.x, SHOCK_FOOT.y + 0.02, SHOCK_FOOT.z));
  // Chain, top and bottom runs, and its slider and guide.
  const sprocketR = 0.105;
  arm.add(steel, beam([-0.075, -0.025, -0.06], [-0.075, ARM.y + sprocketR, ARM.z], 0.012, 0.016));
  arm.add(steel, beam([-0.075, -0.095, -0.06], [-0.075, ARM.y - sprocketR, ARM.z], 0.012, 0.016));
  arm.add(black, beam([-0.085, 0.01, 0.03], [-0.087, -0.06, 0.3], 0.03, 0.02));
  arm.add(black, new BoxGeometry(0.02, 0.07, 0.12).translate(-0.075, ARM.y - 0.12, ARM.z - 0.2));
  // Rear caliper, hanging under the right arm.
  arm.add(darkAlu, new BoxGeometry(0.03, 0.06, 0.07).translate(0.068, ARM.y - 0.07, ARM.z - 0.07));
  arm.into(swingarm);
  const rearWheel = buildWheel(MOTO.rearRadius, MOTO.rearTyre, 0.229, { side: 1, radius: 0.11 }, sprocketR);
  rearWheel.position.copy(ARM);
  swingarm.add(rearWheel);

  // --- the shock: body and reservoir from the top, rod from the foot, spring --
  const shock = new Group();
  shock.position.copy(SHOCK_TOP);
  root.add(shock);
  const body = new Kit();
  body.add(darkAlu, rod([0, 0.02, 0], [0, -0.17, 0], 0.03, 10));
  body.add(anodized, rod([0.045, -0.01, 0.01], [0.045, -0.14, 0.01], 0.021, 8), new CylinderGeometry(0.04, 0.04, 0.012, 10).translate(0, -0.03, 0));
  body.add(darkAlu, new CylinderGeometry(0.02, 0.02, 0.05, 8).rotateZ(Math.PI / 2).translate(0, 0.02, 0));
  body.into(shock);
  const shockRod = new Group();
  shock.add(shockRod);
  const rodKit = new Kit();
  rodKit.add(chrome, rod([0, 0, 0], [0, 0.22, 0], 0.009, 8));
  rodKit.add(darkAlu, new BoxGeometry(0.035, 0.05, 0.035).translate(0, 0.01, 0), new CylinderGeometry(0.04, 0.04, 0.012, 10).translate(0, 0.05, 0));
  rodKit.into(shockRod);
  // A coil of nine turns over a unit length, stretched to fit each frame.
  const turns = 9, coil: V3[] = [];
  for (let i = 0; i <= turns * 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    coil.push([0.036 * Math.cos(a), -i / (turns * 12), 0.036 * Math.sin(a)]);
  }
  const shockSpring = new Mesh(new TubeGeometry(new CatmullRomCurve3(coil.map(vec)), turns * 24, 0.0065, 5, false), plastic);
  shockSpring.castShadow = true;
  shockSpring.position.y = -0.036;
  shock.add(shockSpring);

  return { kind: 'moto', root, steer, forkLower, frontWheel, rearWheel, swingarm, shock, shockRod, shockSpring, headlight };
}

/** What the model needs each frame to move like a bike. */
export interface MotoMotion {
  speed: number;
  steerAngle: number;
  /** Compression, metres, from `updateMotoRide`. */
  front: number;
  rear: number;
  braking: boolean;
  /** 0 day … 1 night. */
  night: number;
  throttle: number;
  airborne: boolean;
}

const wheelRates = new WeakMap<MotoModel, { front: number; rear: number }>();
const shockFoot = new Vector3();
const shockDir = new Vector3();

/**
 * Move the bike's parts for a frame: wheels spinning, bars turned, forks
 * sliding and the swingarm swinging to their compression, the shock's spring
 * squeezed between its mounts, lamps lit.
 */
export function animateMotorbike(model: MotoModel, motion: MotoMotion, dt: number): void {
  // On the ground the wheels roll with the ground; in the air the front
  // freewheels down and the rear revs with the throttle.
  const rates = wheelRates.get(model) ?? { front: 0, rear: 0 };
  const rolling = motion.speed / MOTO.rearRadius;
  if (motion.airborne) {
    rates.front *= Math.exp(-dt * 0.4);
    const rev = motion.throttle > 0 ? rolling * 1.25 + 25 : rolling * 0.6;
    rates.rear += (rev - rates.rear) * (1 - Math.exp(-dt * 3));
  } else {
    rates.front = motion.speed / MOTO.frontRadius;
    rates.rear = rolling;
  }
  wheelRates.set(model, rates);
  model.frontWheel.rotation.x -= rates.front * dt;
  model.rearWheel.rotation.x -= rates.rear * dt;

  model.steer.rotation.y = -motion.steerAngle;
  model.forkLower.position.y = -(FORK_AT_SAG + MOTO.frontSag - motion.front);
  const drop = ARM.y * -1 + (MOTO.rearSag - motion.rear);
  model.swingarm.rotation.x = Math.asin(Math.max(-1, Math.min(1, drop / ARM_LENGTH))) - Math.asin(-ARM.y / ARM_LENGTH);

  // The shock between its top mount and its foot on the swingarm.
  shockFoot.copy(SHOCK_FOOT).applyAxisAngle(X_AXIS, model.swingarm.rotation.x).add(PIVOT);
  shockDir.copy(shockFoot).sub(SHOCK_TOP);
  const length = shockDir.length();
  model.shock.quaternion.setFromUnitVectors(DOWN, shockDir.divideScalar(length));
  model.shockRod.position.y = -length;
  model.shockSpring.scale.y = Math.max(0.05, length - 0.09);

  headlamp.emissiveIntensity = 0.35 + 2.4 * motion.night;
  taillamp.emissiveIntensity = motion.braking ? 2.6 : 0.4 + 1.2 * motion.night;
  model.headlight.intensity = 30 * motion.night;
}

// --- the rider ----------------------------------------------------------------

/** How the rider is sitting, eased between frames. */
export interface RiderState {
  /** 0 seated … 1 standing on the pegs. */
  stand: number;
  /** Inside foot out in a turn: -1 left … 1 right. */
  footOut: number;
  /** Stopped, with the left foot down. */
  footDown: number;
  /** Up on the back wheel, radians, eased. */
  wheelie: number;
  /** The body soaking up a landing: a dip on its own spring. */
  bob: number;
  bobV: number;
  pose: RiderPose;
}

export function createRiderState(): RiderState {
  const v = () => new Vector3();
  return {
    stand: 0, footOut: 0, footDown: 1, wheelie: 0, bob: 0, bobV: 0,
    pose: {
      hips: { x: 0, y: 1.035, z: 0.2, pitch: 0, roll: 0, yaw: 0 },
      torso: { pitch: 0, roll: 0, twist: 0 },
      head: { pitch: 0, yaw: 0 },
      hands: [v(), v()], elbows: [v(), v()], feet: [v(), v()], knees: [v(), v()], toes: [0, 0],
      helmet: true,
    },
  };
}

export interface RiderMotion {
  speed: number;
  steerAngle: number;
  lean: number;
  /** Along the heading, m/s². */
  accel: number;
  airborne: boolean;
  offRoad: boolean;
  /** On the back wheel, radians. */
  wheelie: number;
  /** Vertical speed of a landing this frame, m/s (negative), else 0. */
  landing: number;
}

/**
 * The rider's pose for this frame, in the bike's frame. Also returns the
 * extra lean the bike takes when it is stopped and leaning on the rider's
 * foot, for the renderer to add.
 */
export function riderPose(s: RiderState, m: RiderMotion, dt: number): { pose: RiderPose; bikeLean: number } {
  const v = Math.abs(m.speed);
  const ease = (rate: number) => 1 - Math.exp(-dt * rate);
  // Standing: over rough ground at speed, and in the air. Sitting: on the
  // road, on the back wheel, and in the corners, where the inside foot comes
  // out in a tight turn at moderate speed — the motocross way through one.
  const up = m.wheelie > 0.05;
  const wantStand = m.airborne ? 1 : !up && m.offRoad && v > 9 && Math.abs(m.lean) < 0.3 ? 1 : 0;
  const turning = !m.airborne && v > 2 && v < 22;
  const wantFoot = turning ? Math.sign(m.lean) * smoothstep(0.22, 0.42, Math.abs(m.lean)) : 0;
  const wantDown = !m.airborne && v < 0.6 ? 1 : 0;
  s.stand += (wantStand - s.stand) * ease(5);
  s.footOut += (wantFoot - s.footOut) * ease(6);
  s.footDown += (wantDown - s.footDown) * ease(4);
  s.wheelie += (m.wheelie - s.wheelie) * ease(14);
  if (m.landing < -1) s.bobV += Math.max(-1.6, m.landing * 0.16);
  s.bobV += (-150 * s.bob - 13 * s.bobV) * dt;
  s.bob = Math.max(-0.16, Math.min(0.06, s.bob + s.bobV * dt));

  const p = s.pose;
  const out = Math.abs(s.footOut), outSide = s.footOut < 0 ? 0 : 1;
  const stand = s.stand, air = m.airborne ? 1 : 0;
  // Standing lifts the hips off the seat and back; a foot out slides the
  // rider forward on the seat, weight on the front; on the back wheel the
  // rider slides back and leans over the bars to stay upright in the world.
  const w = s.wheelie;
  // The pro's posture: the back kept straight and the chest up, all the lean
  // from the hips. Seated, the rider sits forward, near the tank, fairly
  // upright; standing — the attack position — the hips go up and back over
  // the pegs and the straight back tips forward over the bars, elbows up.
  p.hips.x = 0;
  p.hips.y = 1.035 + 0.11 * stand + s.bob * stand;
  p.hips.z = 0.12 + 0.08 * stand - 0.06 * out + 0.12 * Math.min(1, w * 2.5) + Math.max(-0.02, Math.min(0.03, m.accel * 0.004));
  p.hips.pitch = 0.1 + 0.32 * stand + w * 0.4;
  // A motocrosser stays more upright than the bike in a turn, and lets the
  // bike move under them in the air.
  p.hips.roll = -m.lean * (0.1 + 0.28 * out + 0.4 * air);
  p.hips.yaw = 0;
  // Over the bars on the throttle, back on the brakes; a landing folds the body.
  p.torso.pitch = 0.2 + Math.max(-0.08, Math.min(0.08, m.accel * 0.01)) - s.bob * 1.2;
  p.torso.roll = 0;
  p.torso.twist = m.steerAngle * 0.35;
  // Head up, eyes up the track and into the turn.
  p.head.pitch = -(p.hips.pitch + p.torso.pitch) + 0.12;
  p.head.yaw = m.steerAngle * 0.5 + m.lean * 0.35;

  for (const i of [0, 1] as const) {
    const side = i === 0 ? -1 : 1;
    gripPoint(side, m.steerAngle, p.hands[i]);
    // Elbows out, and up when standing: the attack position.
    p.elbows[i].set(side * 0.7, 0.3 + 0.4 * stand, 0.6);
    // Ankles over the pegs, balls of the feet on them.
    p.feet[i].set(side * PEG.x, PEG.y + 0.07, PEG.z + 0.04 + 0.02 * stand);
    p.knees[i].set(side * 0.14, 0.2, -1);
    p.toes[i] = 0;
    if (i === outSide && out > 0.01) {
      // The inside foot forward, off the ground, toes up, level with the front axle.
      p.feet[i].lerp(v4.set(side * 0.42, 0.42, -0.36), out);
      p.knees[i].lerp(v4.set(side * 0.6, 0.4, -0.6), out);
      p.toes[i] = 0.35 * out;
    }
    if (i === 0 && s.footDown > 0.01 && !(outSide === 0 && out > 0.1)) {
      p.feet[i].lerp(v4.set(-0.42, 0.12, 0.12), s.footDown);
      p.knees[i].lerp(v4.set(-0.6, 0.2, -0.8), s.footDown);
    }
  }
  return { pose: p, bikeLean: -0.12 * s.footDown };
}

// --- roost: the dirt the rear wheel throws ----------------------------------------

const ROOST_COUNT = 90;
const ROOST_LIFE = 0.9;

/**
 * Clods of earth flung back and up off the rear tyre when it digs in: under
 * power, or sliding, on dirt. One instanced mesh; a clod shrinks away as it
 * falls.
 */
export class Roost {
  readonly mesh: InstancedMesh;
  private readonly pos = new Float32Array(ROOST_COUNT * 3);
  private readonly vel = new Float32Array(ROOST_COUNT * 3);
  private readonly life = new Float32Array(ROOST_COUNT);
  private next = 0;
  private owed = 0;
  private readonly matrix = new Matrix4();
  private readonly spin = new Quaternion();
  private readonly at = new Vector3();
  private readonly size = new Vector3();

  constructor() {
    this.mesh = new InstancedMesh(new IcosahedronGeometry(0.045, 0), faceted(0x6e5236, 0.95), ROOST_COUNT);
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = true;
    const zero = new Matrix4().makeScale(0, 0, 0);
    for (let i = 0; i < ROOST_COUNT; i++) this.mesh.setMatrixAt(i, zero);
  }

  /**
   * Throw clods from the rear contact at (x, y, z), backward from the heading
   * (fx, fz), `rate` per second — 0 for none.
   */
  update(dt: number, x: number, y: number, z: number, fx: number, fz: number, rate: number, speed: number): void {
    this.owed += rate * dt;
    while (this.owed >= 1) {
      this.owed -= 1;
      const i = this.next;
      this.next = (this.next + 1) % ROOST_COUNT;
      const back = 2.5 + Math.random() * 3 + speed * 0.12;
      const side = (Math.random() - 0.5) * 2.2;
      this.pos.set([x + (Math.random() - 0.5) * 0.1, y + 0.05, z + (Math.random() - 0.5) * 0.1], i * 3);
      this.vel.set([-fx * back - fz * side, 1.4 + Math.random() * 2.6, -fz * back + fx * side], i * 3);
      this.life[i] = ROOST_LIFE * (0.6 + Math.random() * 0.4);
    }
    for (let i = 0; i < ROOST_COUNT; i++) {
      if (this.life[i] <= 0) continue;
      this.life[i] -= dt;
      const o = i * 3;
      this.vel[o + 1] -= 9.81 * dt;
      this.pos[o] += this.vel[o] * dt;
      this.pos[o + 1] += this.vel[o + 1] * dt;
      this.pos[o + 2] += this.vel[o + 2] * dt;
      const k = Math.max(0, this.life[i] / ROOST_LIFE);
      this.spin.setFromEuler(e4.set(i + this.life[i] * 9, i * 1.7, this.life[i] * 7));
      const scale = k > 0 ? 0.5 + 0.9 * Math.sqrt(k) * (0.6 + (i % 5) * 0.12) : 0;
      this.mesh.setMatrixAt(i, this.matrix.compose(this.at.set(this.pos[o], this.pos[o + 1], this.pos[o + 2]), this.spin, this.size.setScalar(scale)));
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }
}
